<template>
  <ul v-if="user" class="navbar-list">
    <li class="position-relative">
      <button ref="navbarToggle"
              type="button"
              class="vega-hprofile"
              :class="{ 'is-open': navbarDropdownOpen }"
              aria-haspopup="true"
              :aria-expanded="navbarDropdownOpen ? 'true' : 'false'"
              :aria-label="user.name"
              :title="user.name"
              @click="toggleNavbarDropdown">
        <span class="vega-avatar">
          <img v-if="avatarUrl" :src="avatarUrl" alt="" @error="avatarImageFailed = true">
          <span v-else>{{ user.nameTwoLetter }}</span>
        </span>
        <!-- Çubukta yalnız avatar: ad ve e-posta açılan panelin başında yazıyor, iki kez basılıyordu. -->
      </button>
      <div v-show="navbarDropdownOpen" ref="navbarMenu" class="vega-hmenu" role="menu">
        <div class="vega-hmenu__profile">
          <span class="vega-avatar vega-avatar--lg">
            <img v-if="avatarUrl" :src="avatarUrl" alt="" @error="avatarImageFailed = true">
            <span v-else>{{ user.nameTwoLetter }}</span>
          </span>
          <span class="vega-hmenu__who">
            <span class="vega-hmenu__who-name">{{ user.name }}</span>
            <span v-if="user.username" class="vega-hmenu__who-sub">{{ user.username }}</span>
          </span>
        </div>
        <div class="vega-hmenu__sep"></div>
        <div v-if="colorModeSupported" class="vega-hmenu__mode">
          <span class="vega-hmenu__icon"><i class="fa-regular fa-circle-half-stroke" aria-hidden="true"></i></span>
          <span class="vega-hmenu__mode-label">{{ colorModeTitle }}</span>
          <div class="vega-seg" role="group" :aria-label="colorModeTitle">
            <button v-for="option in colorModeOptions"
                    :key="option.value"
                    type="button"
                    class="vega-seg__btn"
                    :class="{ 'is-active': colorMode === option.value }"
                    :aria-pressed="colorMode === option.value ? 'true' : 'false'"
                    :aria-label="option.label"
                    :title="option.label"
                    @click="chooseColorMode(option.value)">
              <i :class="option.icon" aria-hidden="true"></i>
            </button>
          </div>
        </div>
        <button v-if="hasOpenGettingStartedStep && $te('VegaGettingStarted.MenuItem')" type="button" class="vega-hmenu__item" role="menuitem" @click="openGettingStarted">
          <span class="vega-hmenu__icon"><i class="fa-regular fa-rocket-launch" aria-hidden="true"></i></span>
          <span>{{ $t("VegaGettingStarted.MenuItem") }}</span>
        </button>
        <button type="button" class="vega-hmenu__item" role="menuitem" @click="navigateToStaffProfile">
          <span class="vega-hmenu__icon"><i class="fa-regular fa-user" aria-hidden="true"></i></span>
          <span>{{ $t("ClinicApp.Profile") }}</span>
        </button>
        <button type="button" class="vega-hmenu__item vega-hmenu__item--danger" role="menuitem" @click="logout">
          <span class="vega-hmenu__icon"><i class="fa-regular fa-arrow-right-from-bracket" aria-hidden="true"></i></span>
          <span>{{ $t("Logout") }}</span>
        </button>
      </div>
    </li>
  </ul>
</template>
<script>
  import authService from '../../../services/auth'
  import gettingStartedService from '../../../services/gettingStarted'
  import layoutService from '../../../services/layout'
  import { gettingStartedSteps } from '../../../utils/gettingStarted'
  import { bus } from '../../../plugins/event-bus'
  import navbarDropdown from './navbarDropdown'
  import { getColorMode, setColorMode, isColorModeSupported } from '../../../utils/colorMode'

  export default {
    name: 'UserBar',
    mixins: [navbarDropdown],
    data() {
      return {
        avatarImageFailed: false,
        colorMode: getColorMode(),
        colorModeSupported: isColorModeSupported(),
        gettingStartedMenu: null,
        hasOpenGettingStartedStep: false
      };
    },
    watch: {
      'user.profilePictureUrl'() {
        this.avatarImageFailed = false;
      }
    },
    methods: {
      logout() {
        this.closeNavbarDropdown();
        authService.logout()
      },
      chooseColorMode(mode) {
        this.colorMode = mode;
        setColorMode(mode);
      },
      // "Başlangıç adımları" yalnız bakılmamış adım varken görünür: kartı erken kapatan onu buradan bulur, bitmiş
      // hesapta yeniden açılacak bir şey kalmaz. Adım listesi kartla aynı yardımcıdan (menü + işaretler) çıkar.
      // Durum bilinene ya da okunamazsa satır gizli kalır.
      loadGettingStarted() {
        layoutService.getSideBarMenuItems()
          .catch(() => [])
          .then((menu) => {
            this.gettingStartedMenu = Array.isArray(menu) ? menu : [];
            return gettingStartedService.getStatus();
          })
          .then((status) => this.onGettingStartedMarks((status && status.marks) || []))
          .catch(() => { this.hasOpenGettingStartedStep = false; });
      },
      // İşaretler yalnız ana sayfa kartında değişir; kart yayınca satır sunucuya yeniden sormadan güncellenir.
      onGettingStartedMarks(marks) {
        if (!this.gettingStartedMenu) return;
        this.hasOpenGettingStartedStep = gettingStartedSteps(this.gettingStartedMenu, marks).some(step => !step.done);
      },
      // Ana sayfadaki "Hemen başlayın" kartını gizlenmiş olsa da yeniden açar.
      openGettingStarted() {
        this.closeNavbarDropdown();
        this.$router.push({ name: 'home.dashboard', query: { start: '1' } });
      },
      navigateToStaffProfile() {
        this.closeNavbarDropdown();
        this.$router.push('/staff/profile');
      },
    },
    computed: {
      user: function () {
        return this.$store.getters['Auth/currentStaff'];
      },
      // Çeviri tabloya senkronlanana kadar ham anahtar görünmesin: başlık ve "Sistem" var olan anahtarlara düşer,
      // açık/koyu yalnız simgeyle kalır.
      colorModeTitle() {
        return this.$te('ColorMode.Title') ? this.$t('ColorMode.Title') : this.$t('ViewLayout');
      },
      colorModeOptions() {
        const label = (key, fallback) => (this.$te(key) ? this.$t(key) : fallback);
        return [
          { value: 'light', icon: 'fa-regular fa-sun-bright', label: label('ColorMode.Light', null) },
          { value: 'dark', icon: 'fa-regular fa-moon', label: label('ColorMode.Dark', null) },
          { value: 'system', icon: 'fa-regular fa-display', label: label('ColorMode.System', this.$t('System')) }
        ];
      },
      // Resim yüklenemezse baş harfler gösterilir.
      avatarUrl() {
        return this.user && this.user.profilePictureUrl && !this.avatarImageFailed ? this.user.profilePictureUrl : null;
      }
    },
    mounted() {
      bus.$on('gettingStartedMarks', this.onGettingStartedMarks);
      this.loadGettingStarted();
    },
    beforeUnmount() {
      bus.$off('gettingStartedMarks', this.onGettingStartedMarks);
    }
  }
</script>

<style src="../../../assets/css/header-menu.css"></style>
